import React, { useState, useEffect } from 'react';
import { ethers } from 'ethers';
import WalletConnect from './components/WalletConnect';
import LockForm from './components/LockForm';
import LocksList from './components/LocksList';
import SchedulesList from './components/SchedulesList';
import PlatformInfo from './components/PlatformInfo';
import ToastContainer, { toast } from './components/Toast';
import './App.css';

const CONTRACT_ABI = [
  "function whitelistedTokens(address) view returns (bool)",
  "function lock(address token, uint256 amount, uint256 unlockTime, uint256 savingsGoal) returns (uint256)",
  "function lockTokens(address token, uint256 amount, uint256 unlockTime, address beneficiary, uint256 savingsGoal, bool _flexible, string _name) returns (uint256)",
  "function lockSimple(address token, uint256 amount, uint256 unlockTime) returns (uint256)",
  "function withdraw(uint256 lockId)",
  "function emergencyWithdraw(uint256 lockId)",
  "function emergencyPenaltyBP() view returns (uint256)",
  "function getUserLocks(address user) view returns (uint256[])",
  "function getLockDetails(uint256 lockId) view returns (address token, uint256 amount, uint256 unlockTime, address beneficiary, bool withdrawn, uint256 savingsGoal, uint256 currentSavings, bool flexible, string name)",
  "function isLocked(uint256 lockId) view returns (bool)",
  "function getTimeRemaining(uint256 lockId) view returns (uint256)",
  "function getSavingsProgress(uint256 lockId) view returns (uint256)",
  "function isSavingsGoalReached(uint256 lockId) view returns (bool)",
  "function getSavingsRemaining(uint256 lockId) view returns (uint256)",
  "function isReadyForWithdrawal(uint256 lockId) view returns (bool)",
  "function calculatePlatformFee(uint256 _amount) view returns (uint256)",
  "function getFeeConfiguration() view returns (uint256 percentageBP, uint256 fixedAmount)",
  "function getTotalCostForLock(uint256 _lockAmount) view returns (uint256 totalCost, uint256 platformFee)",
  "function getPlatformStats(address _token) view returns (uint256 totalFeesCollected, uint256 totalLocksCreated, address treasuryAddress, uint256 feePercentageBP, bool feesEnabled)",
  "function addToLock(uint256 lockId, uint256 amount)",
  // Premium features
  "function premiumUsers(address) view returns (bool)",
  "function premiumFeePercentage() view returns (uint256)",
  // Multi-sig
  "function createMultiSigLock(address token, uint256 amount, uint256 unlockTime, address[] approvers, uint256 requiredApprovals, string name) returns (uint256)",
  "function approveMultiSigWithdraw(uint256 lockId)",
  "function getMultiSigConfig(uint256 lockId) view returns (address[] approvers, uint256 requiredApprovals, uint256 currentApprovals, bool isMultiSig)",
  "function hasApproved(uint256 lockId, address approver) view returns (bool)",
  // Scheduled locks
  "function createSchedule(address token, uint256 amount, uint256 interval, uint256 lockDuration, uint256 totalExecutions, string name) returns (uint256)",
  "function executeScheduledLock(uint256 scheduleId) returns (uint256)",
  "function cancelSchedule(uint256 scheduleId)",
  "function getUserSchedules(address user) view returns (uint256[])",
  "function getScheduleDetails(uint256 scheduleId) view returns (address creator, address token, uint256 amount, uint256 interval, uint256 lockDuration, uint256 totalExecutions, uint256 executedCount, uint256 nextExecutionTime, bool active, string name)",
  // ERC721
  "function ownerOf(uint256 tokenId) view returns (address)",
  "function balanceOf(address owner) view returns (uint256)",
  "function name() view returns (string)",
  "function symbol() view returns (string)",
  "function tokenURI(uint256 tokenId) view returns (string)",
];

const STABLECOINS = {
  USDC: { address: "0xC7de83aC4c2971dCA2828EA0782b5A9706d30598", decimals: 6 },
  USDT: { address: "0x9e00a7F73E9782E20a28898eB80400dC4Ea33e2b", decimals: 6 },
  DAI: { address: "0x8c00Df228Db94e67ef03dFDaDcD68C2931c0EfFa", decimals: 18 },
  EURC: { address: "0x8dde1DE10Af0d0B69E869076D8804d84d1f3F099", decimals: 6 },
};

function App() {
  const [connected, setConnected] = useState(false);
  const [account, setAccount] = useState('');
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [contract, setContract] = useState(null);
  const [locks, setLocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [platformStats, setPlatformStats] = useState(null);
  const [isPremium, setIsPremium] = useState(false);
  const [schedules, setSchedules] = useState([]);

  // Initialize Web3
  const initializeWeb3 = async () => {
    if (typeof window.ethereum !== 'undefined') {
      try {
        // Force switch to Sepolia network
        try {
          await window.ethereum.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: '0xaa36a7' }],
          });
        } catch (switchError) {
          if (switchError.code === 4902) {
            await window.ethereum.request({
              method: 'wallet_addEthereumChain',
              params: [{
                chainId: '0xaa36a7',
                chainName: 'Sepolia Testnet',
                nativeCurrency: { name: 'SepoliaETH', symbol: 'ETH', decimals: 18 },
                rpcUrls: ['https://rpc.sepolia.org'],
                blockExplorerUrls: ['https://sepolia.etherscan.io'],
              }],
            });
          }
        }

        const accounts = await window.ethereum.request({
          method: 'eth_requestAccounts',
        });
        
        const web3Provider = new ethers.BrowserProvider(window.ethereum);
        const web3Signer = await web3Provider.getSigner();
        
        setProvider(web3Provider);
        setSigner(web3Signer);
        setAccount(accounts[0]);
        
        // Initialize contract (you'll need to replace this with your deployed address)
        const contractAddress = process.env.REACT_APP_CONTRACT_ADDRESS || "0x"; // Set via .env
        const timelockContract = new ethers.Contract(
          contractAddress,
          CONTRACT_ABI,
          web3Signer
        );
        setContract(timelockContract);
        setConnected(true);
        
        await loadUserLocks(accounts[0], timelockContract);
        await loadPremiumStatus(accounts[0], timelockContract);
        await loadSchedules(accounts[0], timelockContract);
      } catch (error) {
        console.error('Error connecting wallet:', error);
        toast.error('Failed to connect wallet');
      }
    } else {
      toast.warning('Please install MetaMask or another Web3 wallet');
    }
  };

  const loadUserLocks = async (userAddress, contractInstance) => {
    if (!contractInstance) return;
    
    try {
      setLoading(true);
      const lockIds = await contractInstance.getUserLocks(userAddress);
      
      const locksData = await Promise.all(
        lockIds.map(async (id) => {
          const details = await contractInstance.getLockDetails(id);
          const isActive = await contractInstance.isLocked(id);
          const timeRemaining = await contractInstance.getTimeRemaining(id);
          const isReady = await contractInstance.isReadyForWithdrawal(id);
          const savingsProgress = await contractInstance.getSavingsProgress(id);
          const savingsRemaining = await contractInstance.getSavingsRemaining(id);
          const multiSigConfig = await contractInstance.getMultiSigConfig(id);

          // Determine decimals from token address
          const tokenDecimals = Object.values(STABLECOINS).find(
            t => t.address.toLowerCase() === details.token.toLowerCase()
          )?.decimals || 6;
          
          return {
            id: id.toString(),
            token: details.token,
            amount: ethers.formatUnits(details.amount, tokenDecimals),
            unlockTime: details.unlockTime.toString(),
            beneficiary: details.beneficiary,
            withdrawn: details.withdrawn,
            isActive,
            timeRemaining: timeRemaining.toString(),
            savingsGoal: ethers.formatUnits(details.savingsGoal, tokenDecimals),
            currentSavings: ethers.formatUnits(details.currentSavings, tokenDecimals),
            savingsProgress: savingsProgress.toString(),
            savingsRemaining: ethers.formatUnits(savingsRemaining, tokenDecimals),
            isReady,
            flexible: details.flexible,
            name: details.name,
            multiSig: multiSigConfig.isMultiSig ? {
              approvers: multiSigConfig.approvers,
              required: Number(multiSigConfig.requiredApprovals),
              current: Number(multiSigConfig.currentApprovals),
            } : null,
            nftImage: null,
          };
        })
      );
      
      setLocks(locksData);

      // Fetch NFT images in background (don't block UI)
      locksData.forEach(async (lock, index) => {
        try {
          const uri = await contractInstance.tokenURI(lock.id);
          if (uri && uri.startsWith('data:application/json;base64,')) {
            const json = JSON.parse(atob(uri.replace('data:application/json;base64,', '')));
            if (json.image) {
              setLocks(prev => prev.map(l => l.id === lock.id ? { ...l, nftImage: json.image } : l));
            }
          }
        } catch (err) {
          // tokenURI may not exist on older contracts
        }
      });

      // Load platform stats for USDC (can be extended for other tokens)
      try {
        const stats = await contractInstance.getPlatformStats(STABLECOINS.USDC.address);
        setPlatformStats({
          totalFeesCollected: ethers.formatUnits(stats.totalFeesCollected, 6),
          totalLocksCreated: stats.totalLocksCreated.toString(),
          treasuryAddress: stats.treasuryAddress,
          feePercentageBP: stats.feePercentageBP.toString(),
          feesEnabled: stats.feesEnabled,
        });
      } catch (err) {
        console.log('Platform stats not available:', err.message);
      }
    } catch (error) {
      console.error('Error loading locks:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLockCreated = async () => {
    if (contract && account) {
      await loadUserLocks(account, contract);
    }
  };

  const handleWithdraw = async (lockId) => {
    if (!contract) {
      toast.error('Contract not initialized');
      return;
    }

    try {
      setLoading(true);
      toast.info('Withdrawing... please confirm in MetaMask');
      const tx = await contract.withdraw(lockId);
      toast.info('Transaction submitted, waiting for confirmation...');
      await tx.wait();
      toast.success('Successfully withdrawn!');
      await handleLockCreated();
    } catch (error) {
      console.error('Error withdrawing:', error);
      toast.error('Withdrawal failed: ' + (error.reason || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleAddFunds = async (lockId, amount, tokenAddress) => {
    if (!contract || !signer) {
      toast.error('Contract not initialized');
      return;
    }

    try {
      setLoading(true);
      toast.info('Adding funds... please confirm in MetaMask');
      const tokenDecimals = Object.values(STABLECOINS).find(t => t.address.toLowerCase() === tokenAddress.toLowerCase())?.decimals || 6;
      const amountInWei = ethers.parseUnits(amount, tokenDecimals);

      // Calculate fee
      const fee = await contract.calculatePlatformFee(amountInWei);
      const totalCost = amountInWei + fee;

      // Approve
      const ERC20_ABI = ["function approve(address spender, uint256 amount) returns (bool)"];
      const tokenContract = new ethers.Contract(tokenAddress, ERC20_ABI, signer);
      const approveTx = await tokenContract.approve(await contract.getAddress(), totalCost);
      await approveTx.wait();

      // Add to lock
      const tx = await contract.addToLock(lockId, amountInWei);
      await tx.wait();
      toast.success(`Successfully added ${amount} to lock #${lockId}`);
      await handleLockCreated();
    } catch (error) {
      console.error('Error adding funds:', error);
      toast.error('Failed to add funds: ' + (error.reason || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleEmergencyWithdraw = async (lockId) => {
    if (!contract) {
      toast.error('Contract not initialized');
      return;
    }

    try {
      setLoading(true);
      toast.warning('Emergency withdrawal... penalty will be deducted. Confirm in MetaMask.');
      const tx = await contract.emergencyWithdraw(lockId);
      toast.info('Transaction submitted, waiting for confirmation...');
      await tx.wait();
      toast.success('Emergency withdrawal complete. Penalty was deducted.');
      await handleLockCreated();
    } catch (error) {
      console.error('Error emergency withdrawing:', error);
      toast.error('Emergency withdrawal failed: ' + (error.reason || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Load premium status
  const loadPremiumStatus = async (userAddress, contractInstance) => {
    try {
      const premium = await contractInstance.premiumUsers(userAddress);
      setIsPremium(premium);
    } catch (err) {
      console.log('Premium status not available:', err.message);
    }
  };

  // Load user schedules
  const loadSchedules = async (userAddress, contractInstance) => {
    try {
      const scheduleIds = await contractInstance.getUserSchedules(userAddress);
      const schedulesData = await Promise.all(
        scheduleIds.map(async (id) => {
          const s = await contractInstance.getScheduleDetails(id);
          const tokenDecimals = Object.values(STABLECOINS).find(
            t => t.address.toLowerCase() === s.token.toLowerCase()
          )?.decimals || 6;
          return {
            id: id.toString(),
            creator: s.creator,
            token: s.token,
            amount: ethers.formatUnits(s.amount, tokenDecimals),
            interval: Number(s.interval),
            lockDuration: Number(s.lockDuration),
            totalExecutions: Number(s.totalExecutions),
            executedCount: Number(s.executedCount),
            nextExecutionTime: Number(s.nextExecutionTime),
            active: s.active,
            name: s.name,
          };
        })
      );
      setSchedules(schedulesData);
    } catch (err) {
      console.log('Schedules not available:', err.message);
    }
  };

  // Multi-sig approval handler
  const handleApproveMultiSig = async (lockId) => {
    if (!contract) return;
    try {
      setLoading(true);
      toast.info('Approving multi-sig... confirm in MetaMask');
      const tx = await contract.approveMultiSigWithdraw(lockId);
      await tx.wait();
      toast.success(`Approved withdrawal for lock #${lockId}`);
      await handleLockCreated();
    } catch (error) {
      console.error('Error approving multi-sig:', error);
      toast.error('Approval failed: ' + (error.reason || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Execute scheduled lock handler
  const handleExecuteSchedule = async (scheduleId) => {
    if (!contract || !signer) return;
    try {
      setLoading(true);

      // Get schedule details for approval
      const s = await contract.getScheduleDetails(scheduleId);
      const tokenDecimals = Object.values(STABLECOINS).find(
        t => t.address.toLowerCase() === s.token.toLowerCase()
      )?.decimals || 6;

      const fee = await contract.calculatePlatformFee(s.amount);
      const totalCost = s.amount + fee;

      const ERC20_ABI = ["function approve(address spender, uint256 amount) returns (bool)"];
      const tokenContract = new ethers.Contract(s.token, ERC20_ABI, signer);
      
      toast.info('Approving tokens for scheduled lock...');
      const approveTx = await tokenContract.approve(await contract.getAddress(), totalCost);
      await approveTx.wait();

      toast.info('Executing scheduled lock...');
      const tx = await contract.executeScheduledLock(scheduleId);
      await tx.wait();
      toast.success('Scheduled lock executed!');
      await handleLockCreated();
      await loadSchedules(account, contract);
    } catch (error) {
      console.error('Error executing schedule:', error);
      toast.error('Execution failed: ' + (error.reason || error.message));
    } finally {
      setLoading(false);
    }
  };

  // Cancel schedule handler
  const handleCancelSchedule = async (scheduleId) => {
    if (!contract) return;
    try {
      setLoading(true);
      const tx = await contract.cancelSchedule(scheduleId);
      await tx.wait();
      toast.success('Schedule cancelled');
      await loadSchedules(account, contract);
    } catch (error) {
      console.error('Error cancelling schedule:', error);
      toast.error('Cancel failed: ' + (error.reason || error.message));
    } finally {
      setLoading(false);
    }
  };

  const contractAddress = process.env.REACT_APP_CONTRACT_ADDRESS || '';

  return (
    <div className="app">
      <ToastContainer />
      <header className="header">
        <h1>LokFi</h1>
        {connected && (
          <div className="header-account">
            <span className="account-dot" />
            <span>{account.substring(0, 6)}...{account.substring(38)}</span>
            {isPremium && <span className="premium-badge">Premium</span>}
          </div>
        )}
      </header>

      <main className="container">
        {!connected ? (
          <WalletConnect onConnect={initializeWeb3} contractAddress={contractAddress} />
        ) : (
          <>
            <PlatformInfo platformStats={platformStats} />

            <div className="content">
              <div className="form-section">
                <h2>Create a Lock</h2>
                <LockForm
                  contract={contract}
                  signer={signer}
                  stablecoins={STABLECOINS}
                  onLockCreated={handleLockCreated}
                  disabled={loading}
                />
              </div>

              <div className="locks-section">
                <h2>Your Locks {locks.length > 0 && <span className="lock-count">{locks.length}</span>}</h2>
                {loading ? (
                  <div className="loading-state">
                    <span className="spinner" /> Loading your locks...
                  </div>
                ) : locks.length > 0 ? (
                  <LocksList
                    locks={locks}
                    onWithdraw={handleWithdraw}
                    onAddFunds={handleAddFunds}
                    onEmergencyWithdraw={handleEmergencyWithdraw}
                    onApproveMultiSig={handleApproveMultiSig}
                    account={account}
                    disabled={loading}
                  />
                ) : (
                  <div className="empty-state">
                    <span className="empty-icon">🔒</span>
                    <p>No locks yet</p>
                    <small>Create your first lock to get started</small>
                  </div>
                )}
              </div>

              {schedules.length > 0 && (
                <div className="locks-section">
                  <h2>Scheduled Locks</h2>
                  <SchedulesList
                    schedules={schedules}
                    onExecute={handleExecuteSchedule}
                    onCancel={handleCancelSchedule}
                    disabled={loading}
                  />
                </div>
              )}
            </div>

            <div className="transparency-section">
              <h2>Security & Transparency</h2>
              <div className="transparency-grid">
                <div className="transparency-card">
                  <div className="transparency-header">
                    <span className="transparency-icon">🛡️</span>
                    <strong>Non-Custodial</strong>
                  </div>
                  <p>Your tokens are held by the smart contract, not by us. No person or team has access to locked funds.</p>
                </div>
                <div className="transparency-card">
                  <div className="transparency-header">
                    <span className="transparency-icon">🔍</span>
                    <strong>Verified Source Code</strong>
                  </div>
                  <p>
                    The contract is verified and readable by anyone.{' '}
                    {contractAddress && (
                      <a href={`https://sepolia.etherscan.io/address/${contractAddress}#code`} target="_blank" rel="noopener noreferrer">
                        Read it on Etherscan →
                      </a>
                    )}
                  </p>
                </div>
                <div className="transparency-card">
                  <div className="transparency-header">
                    <span className="transparency-icon">🚫</span>
                    <strong>No Admin Backdoors</strong>
                  </div>
                  <p>The owner can change fees and whitelist tokens. The owner <strong>cannot</strong> access your funds, change unlock times, or withdraw your tokens.</p>
                </div>
                <div className="transparency-card">
                  <div className="transparency-header">
                    <span className="transparency-icon">🎫</span>
                    <strong>On-Chain Receipts</strong>
                  </div>
                  <p>Every lock mints an NFT receipt to your wallet — cryptographic proof that your funds are secured.</p>
                </div>
              </div>
              {contractAddress && (
                <div className="contract-address-bar">
                  <span>Contract:</span>
                  <a href={`https://sepolia.etherscan.io/address/${contractAddress}`} target="_blank" rel="noopener noreferrer">
                    {contractAddress}
                  </a>
                </div>
              )}
            </div>
          </>
        )}
      </main>

      <footer className="footer">
        <div className="footer-trust">
          <span>Non-custodial</span>
          <span className="footer-dot">·</span>
          {contractAddress && (
            <>
              <a href={`https://sepolia.etherscan.io/address/${contractAddress}#code`} target="_blank" rel="noopener noreferrer">
                Verified on Etherscan
              </a>
              <span className="footer-dot">·</span>
            </>
          )}
          <span>Your keys, your funds</span>
        </div>
      </footer>
    </div>
  );
}

export default App;
